/**
 * 報價單 Excel 匯出「模板二：預購表用」（需求 R168）
 *
 * PM 2026-09-12 拍板 Q1～Q7 全 A：
 *   Q1=A 淨重／毛重從產品主檔即時讀（getById populate 補 netWeight grossWeight；本票不碰寫入路徑）
 *   Q2=A 單價／箱單價為**純數值 + 幣別 numFmt**（Excel 可加總），值＝畫面印出的單價與 需求 R165 箱價
 *   Q3=A 材積 I／J 帶單位文字「2.18cuft」「0.062CBM」
 *   Q4=A 兩個入口的選單改三顆：PDF 標準報價單／Excel 標準報價單／Excel 預購表用
 *   Q5=A 檔名 `{報價單號}_預購表.xlsx`
 *   Q6=A A4 橫式（15 欄壓進直式字會極小）
 *   Q7=A 匯出成功即 markAsExported（沿用 需求 R119 Q7=A）
 *
 * 版面依附件「報價單格式(2).xlsx」第 11／12 列（拆欄後的樣板列）：第 1～9 列頁首／單據／客戶資料
 * 與標準版相同，只是合併範圍拉寬到 O 欄；第 13 列以後是舊 6 欄排版殘留，不視為規格。
 *
 * 與 quoteExcelExport.js 的關係：
 *   標準版被 需求 R134／216 fixture 鎖住欄寬／列高／圖片置中，**本檔不改它任何輸出**；
 *   共用 helper（fetchCompanyInfo／fetchImageData／fitContain／colWidthToPx／pxToNativeCol／
 *   pxToNativeRow）與色票／字級／字型常數一律從該檔 import，不複製、不改行為。
 *   15 欄的欄寬與表頭是本檔獨立一份常數，改標準版不會波及這裡、反之亦然。
 */

import {
  C, SZ, FONT, CURRENCY_LABEL, fmt, fmtDate,
  fetchCompanyInfo, fetchImageData, fitContain,
  colWidthToPx, pxToNativeCol, pxToNativeRow,
  LOGO_BOX_PX, ITEM_THUMB_PX,
} from './quoteExcelExport';
import { buildRemarkText } from './quoteRemark';
import { ceilMoney, calcBoxPrice, moneyDecimals } from './quoteMoney';
import { getItemShelfLife } from './quoteShelfLife';

// 15 欄（A～O）的欄寬（字元）— 逐格抄自附件第 11／12 列；sheet.columns 與圖片置中換算共用這一份。
export const PREORDER_COL_WIDTHS_CH = [14, 26, 26, 16, 13.5, 13.5, 11, 11, 9, 9.875, 13, 16, 16, 23, 23];
export const PREORDER_LAST_COL = PREORDER_COL_WIDTHS_CH.length; // 15 = O

// 表頭（第 11 列）：中文／英文逐字照附件；I11:J11 材積合併成一格。
export const PREORDER_HEADERS = [
  { c1: 1,  c2: 1,  zh: '產品圖片',        en: 'Product Image',     align: 'center' },
  { c1: 2,  c2: 2,  zh: '產品名稱(中文)',  en: 'Description',       align: 'left' },
  { c1: 3,  c2: 3,  zh: '產品名稱(英文)',  en: 'Description',       align: 'left' },
  { c1: 4,  c2: 4,  zh: '規格',            en: 'Spec' },
  { c1: 5,  c2: 5,  zh: '箱入數',          en: 'Units per Carton' },
  { c1: 6,  c2: 6,  zh: '保存期限',        en: 'Shelf Life' },
  { c1: 7,  c2: 7,  zh: '淨重',            en: 'N.W.' },
  { c1: 8,  c2: 8,  zh: '毛重',            en: 'G.W.' },
  { c1: 9,  c2: 10, zh: '材積',            en: 'Cuft / CBM' },
  { c1: 11, c2: 11, zh: '條碼',            en: 'Barcode' },
  { c1: 12, c2: 12, zh: '單價',            en: 'Unit Price / CTN' },
  { c1: 13, c2: 13, zh: '箱單價',          en: 'CTN Price' },
  { c1: 14, c2: 14, zh: 'MOQ',             en: 'MOQ' },
  { c1: 15, c2: 15, zh: '備註',            en: 'Remark' },
];

// 與標準版相同的列高（需求 R135：品項列 108pt；其餘列高一律沿用）。
const ITEM_ROW_HEIGHT = 108;
const LOGO_ROWS_PT = [30, 22, 20, 20];

// Q2=A：幣別 numFmt —— 位數跟 quoteMoney.moneyDecimals 同源（TWD/HKD 1 位、USD/EUR 2 位），
// 幣別前綴跟標準版 CURRENCY_LABEL 同源。附件 L12 原文：`"US$"#,##0.00`。
export function moneyNumFmt(currency) {
  const label = CURRENCY_LABEL[currency] || currency || '';
  const d = moneyDecimals(currency);
  return `"${label}"#,##0${d > 0 ? '.' + '0'.repeat(d) : ''}`;
}

// Q1=A：淨重／毛重顯示「{數值} kgs」，沒填顯示「—」。
export function fmtKgs(v) {
  if (v === null || v === undefined || v === '') return '—';
  const n = parseFloat(v);
  return Number.isFinite(n) ? `${n} kgs` : '—';
}

export async function exportQuotePreorderExcel({ quote }) {
  const { Workbook } = await import('exceljs');

  const co = await fetchCompanyInfo(quote.companyHeader);
  const items = quote.items || [];
  const contact = quote.contactPerson || {};
  const currency = quote.currency || 'TWD';
  const custContact = (quote.customer?.contacts || [])[0] || {};
  const custPhone = custContact.phone || '';
  const custEmail = custContact.email || '';
  const numFmt = moneyNumFmt(currency);

  const logoData = co.logoUrl ? await fetchImageData(co.logoUrl) : null;
  const productImgMap = {};
  await Promise.all(items.map(async (item, idx) => {
    const img = item.product?.productImage;
    if (img) {
      const data = await fetchImageData(img);
      if (data) productImgMap[idx] = data;
    }
  }));

  const workbook = new Workbook();
  const sheet = workbook.addWorksheet('報價單', {
    pageSetup: {
      paperSize: 9, // A4
      orientation: 'landscape', // Q6=A
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      horizontalCentered: true,
      margins: { left: 0.3, right: 0.3, top: 0.3, bottom: 0.3, header: 0, footer: 0 },
    },
  });

  sheet.columns = PREORDER_COL_WIDTHS_CH.map((width) => ({ width }));
  // ExcelJS 把 width === 9 視為「預設欄寬」而不寫 <col customWidth>，I 欄（附件 9ch）會退回 Excel
  // 預設的 8.43。改把工作表預設欄寬設成 9，I 欄在 Excel 內就真的是 9ch（其餘 14 欄都有各自的 <col>）。
  sheet.properties.defaultColWidth = 9;

  const LAST = PREORDER_LAST_COL;
  const solid = (argb) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb } });
  const bs = (argb, style = 'thin') => ({ style, color: { argb } });
  const cl = (r, c) => sheet.getCell(r, c);
  const fillRect = (r1, c1, r2, c2, argb) => {
    for (let r = r1; r <= r2; r++) {
      for (let c = c1; c <= c2; c++) cl(r, c).fill = solid(argb);
    }
  };

  // HEADER（同標準版，合併範圍拉寬到 O 欄）
  // 左半 B:H（公司名／英文名／地址／Tel-Fax）、右半 I:O（標題色塊／聯絡人／Email）。
  const LEFT_END = 8;   // H
  const RIGHT_START = 9; // I
  fillRect(1, 1, 4, LAST, C.light);
  LOGO_ROWS_PT.forEach((h, i) => { sheet.getRow(i + 1).height = h; });

  sheet.mergeCells(1, 1, 4, 1);
  const logoCell = cl(1, 1);
  logoCell.fill = solid(C.light);
  logoCell.alignment = { vertical: 'middle', horizontal: 'center' };
  if (logoData) {
    const logoId = workbook.addImage({ buffer: logoData.buffer, extension: logoData.extension });
    const cellW = colWidthToPx(PREORDER_COL_WIDTHS_CH[0]);
    const cellH = LOGO_ROWS_PT.reduce((s, h) => s + h, 0) * 4 / 3;
    const { width: lw, height: lh } = fitContain(logoData.naturalWidth, logoData.naturalHeight, LOGO_BOX_PX, LOGO_BOX_PX);
    const tlCol = pxToNativeCol(0, [PREORDER_COL_WIDTHS_CH[0]], (cellW - lw) / 2);
    const tlRow = pxToNativeRow(0, LOGO_ROWS_PT, (cellH - lh) / 2);
    sheet.addImage(logoId, { tl: { ...tlCol, ...tlRow }, ext: { width: lw, height: lh }, editAs: 'oneCell' });
  }

  const writeLeft = (r, value, font) => {
    sheet.mergeCells(r, 2, r, LEFT_END);
    const cell = cl(r, 2);
    cell.value = value;
    cell.fill = solid(C.light);
    cell.font = font;
    cell.alignment = { vertical: 'middle', horizontal: 'left' };
    return cell;
  };
  writeLeft(1, co.name || '', { ...FONT, size: SZ.h1, bold: true, color: { argb: C.dark } });
  writeLeft(2, co.nameEn || '', { ...FONT, size: SZ.body, color: { argb: C.mid } });
  writeLeft(3, [co.address, co.addressEn].filter(Boolean).join(' / '), { ...FONT, size: SZ.small, color: { argb: C.mid } });
  writeLeft(4, [co.tel ? `Tel: ${co.tel}` : '', co.fax ? `Fax: ${co.fax}` : ''].filter(Boolean).join('   '),
    { ...FONT, size: SZ.small, color: { argb: C.mid } });

  sheet.mergeCells(1, RIGHT_START, 1, LAST);
  const tt = cl(1, RIGHT_START);
  tt.value = '報價單｜QUOTATION';
  tt.fill = solid(C.badge);
  tt.font = { ...FONT, size: SZ.h2, bold: true, color: { argb: C.dark } };
  tt.alignment = { vertical: 'middle', horizontal: 'center' };

  sheet.mergeCells(2, RIGHT_START, 2, LAST);
  const ctn = cl(2, RIGHT_START);
  ctn.fill = solid(C.light);
  ctn.alignment = { vertical: 'middle', horizontal: 'right', wrapText: true };
  if (contact.name) {
    ctn.value = {
      richText: [
        { text: '聯絡人 Contact ', font: { ...FONT, size: SZ.small, color: { argb: C.mid } } },
        { text: contact.name, font: { ...FONT, size: SZ.body, bold: true, color: { argb: C.dark } } },
      ],
    };
  }

  sheet.mergeCells(3, RIGHT_START, 3, LAST);
  const ctp = cl(3, RIGHT_START);
  ctp.fill = solid(C.light);
  ctp.alignment = { vertical: 'middle', horizontal: 'right' };
  if (contact.email) {
    ctp.value = contact.email;
    ctp.font = { ...FONT, size: SZ.small, color: { argb: C.mid } };
  }

  sheet.mergeCells(4, RIGHT_START, 4, LAST);
  cl(4, RIGHT_START).fill = solid(C.light);

  sheet.getRow(5).height = 4;
  fillRect(5, 1, 5, LAST, C.badge);

  // 雙欄資訊區（單據資料 A:G ／ 客戶資料 H:O）
  const infoBorder = { top: bs(C.border), left: bs(C.border), right: bs(C.border), bottom: bs(C.border) };
  const INFO_L_LABEL = 1, INFO_L_V1 = 2, INFO_L_V2 = 7;   // A ／ B:G
  const INFO_R_LABEL = 8, INFO_R_V1 = 9, INFO_R_V2 = LAST; // H ／ I:O

  sheet.getRow(6).height = 22;
  sheet.mergeCells(6, INFO_L_LABEL, 6, INFO_L_V2);
  const lh = cl(6, INFO_L_LABEL);
  lh.value = '單據資料';
  lh.fill = solid(C.light);
  lh.font = { ...FONT, size: SZ.h3, bold: true, color: { argb: C.mid } };
  lh.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  lh.border = infoBorder;
  sheet.mergeCells(6, INFO_R_LABEL, 6, INFO_R_V2);
  const rh = cl(6, INFO_R_LABEL);
  rh.value = '客戶資料';
  rh.fill = solid(C.light);
  rh.font = { ...FONT, size: SZ.h3, bold: true, color: { argb: C.mid } };
  rh.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  rh.border = infoBorder;

  const writeInfoCell = (r, c1, c2, value, color) => {
    if (c1 !== c2) sheet.mergeCells(r, c1, r, c2);
    const cell = cl(r, c1);
    cell.value = value;
    cell.fill = solid(C.light);
    cell.font = { ...FONT, size: SZ.body, bold: true, color: { argb: color } };
    cell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
    cell.border = infoBorder;
  };
  const writeInfoRow = (r, leftLabel, leftVal, rightLabel, rightVal) => {
    sheet.getRow(r).height = 20;
    writeInfoCell(r, INFO_L_LABEL, INFO_L_LABEL, leftLabel, C.mid);
    writeInfoCell(r, INFO_L_V1, INFO_L_V2, leftVal, C.dark);
    writeInfoCell(r, INFO_R_LABEL, INFO_R_LABEL, rightLabel, C.mid);
    writeInfoCell(r, INFO_R_V1, INFO_R_V2, rightVal, C.dark);
  };
  writeInfoRow(7, '報價單編號', quote.quoteNumber || '—', '客戶名稱', quote.customerName || '—');
  writeInfoRow(8, '報價日期', fmtDate(quote.quoteDate), '聯絡電話', custPhone || '—');
  writeInfoRow(9, '有效期限', fmtDate(quote.expiryDate), '電子信箱', custEmail || '—');

  // 品項表（15 欄）
  sheet.getRow(10).height = 6;
  const tableHeaderRow = 11;
  sheet.getRow(tableHeaderRow).height = 36;
  const headerBorder = {
    top: bs(C.border), left: bs(C.border), right: bs(C.border), bottom: bs(C.badge, 'medium'),
  };
  PREORDER_HEADERS.forEach(({ c1, c2, zh, en, align = 'center' }) => {
    if (c1 !== c2) sheet.mergeCells(tableHeaderRow, c1, tableHeaderRow, c2);
    for (let c = c1; c <= c2; c++) {
      cl(tableHeaderRow, c).fill = solid(C.headerBg);
      cl(tableHeaderRow, c).border = headerBorder;
    }
    const cell = cl(tableHeaderRow, c1);
    cell.value = {
      richText: [
        { text: zh, font: { ...FONT, size: SZ.th, bold: true, color: { argb: C.dark } } },
        { text: '\n' + en, font: { ...FONT, size: SZ.thEn, color: { argb: C.mid } } },
      ],
    };
    cell.alignment = { vertical: 'middle', horizontal: align, wrapText: true, indent: align === 'left' ? 1 : 0 };
  });

  const dataBorder = {
    top: bs(C.border, 'hair'), left: bs(C.border, 'hair'), right: bs(C.border, 'hair'), bottom: bs(C.border),
  };
  let curRow = tableHeaderRow + 1;

  // 文字格共用寫法：白底、置中（可覆寫）、wrapText、hair 框。
  const writeText = (r, c, value, font, align = 'center', extra = {}) => {
    const cell = cl(r, c);
    cell.value = value;
    cell.fill = solid(C.white);
    cell.font = font;
    cell.alignment = { vertical: 'middle', horizontal: align, wrapText: true, indent: align === 'left' ? 1 : 0 };
    cell.border = dataBorder;
    Object.assign(cell, extra);
    return cell;
  };

  items.forEach((item, idx) => {
    sheet.getRow(curRow).height = ITEM_ROW_HEIGHT;

    // A：產品圖片（規則同標準版：ITEM_THUMB_PX 見方置中；無圖片顯示「產品圖」placeholder）
    const imgCell = cl(curRow, 1);
    imgCell.alignment = { vertical: 'middle', horizontal: 'center' };
    imgCell.border = dataBorder;
    imgCell.fill = solid(C.white);
    const imgData = productImgMap[idx];
    if (imgData) {
      const imgId = workbook.addImage({ buffer: imgData.buffer, extension: imgData.extension });
      const cellW = colWidthToPx(PREORDER_COL_WIDTHS_CH[0]);
      const cellH = ITEM_ROW_HEIGHT * 4 / 3;
      const { width: pw, height: ph } = fitContain(imgData.naturalWidth, imgData.naturalHeight, ITEM_THUMB_PX, ITEM_THUMB_PX);
      const tlCol = pxToNativeCol(0, [PREORDER_COL_WIDTHS_CH[0]], (cellW - pw) / 2);
      const tlRow = pxToNativeRow(curRow - 1, [ITEM_ROW_HEIGHT], (cellH - ph) / 2);
      sheet.addImage(imgId, { tl: { ...tlCol, ...tlRow }, ext: { width: pw, height: ph }, editAs: 'oneCell' });
    } else {
      imgCell.value = '產品圖';
      imgCell.font = { ...FONT, size: SZ.caption, color: { argb: C.mid } };
    }

    // B：貨編 + 換行 + 中文品名（附件 B12「V031001\n珍福-魷魚絲(原味)」）
    const code = item.productCode || item.product?.productCode || '';
    const nameZh = item.productName || '—';
    const richParts = [];
    if (code) richParts.push({ text: code, font: { ...FONT, size: SZ.code, color: { argb: C.codeText } } });
    richParts.push({ text: (code ? '\n' : '') + nameZh, font: { ...FONT, size: SZ.prodZh, bold: true, color: { argb: C.dark } } });
    writeText(curRow, 2, { richText: richParts }, undefined, 'left');

    // C：英文品名單獨一欄
    writeText(curRow, 3, item.englishName || '—', { ...FONT, size: SZ.prodEn, color: { argb: C.mid } }, 'left');

    // D：規格原值（不再拼「x N入/箱」）；E：箱入數純數字；F：保存期限（與標準版同一來源）
    writeText(curRow, 4, item.spec || '—', { ...FONT, size: SZ.spec, color: { argb: C.dark } });
    const pcs = parseFloat(item.pcsPerBox);
    writeText(curRow, 5, Number.isFinite(pcs) ? pcs : '—', { ...FONT, size: SZ.spec, color: { argb: C.dark } });
    writeText(curRow, 6, getItemShelfLife(item) || '—', { ...FONT, size: SZ.spec, color: { argb: C.dark } });

    // G／H：淨重／毛重（Q1=A：讀 populate 回來的產品主檔；編輯頁匯出由 buildExportQuoteObj 帶入）
    writeText(curRow, 7, fmtKgs(item.product?.netWeight), { ...FONT, size: SZ.spec, color: { argb: C.dark } });
    writeText(curRow, 8, fmtKgs(item.product?.grossWeight), { ...FONT, size: SZ.spec, color: { argb: C.dark } });

    // I／J：材積 cuft／CBM 拆兩格（Q3=A 帶單位文字）；算法與標準版同：cuft 2 位、CBM=cuft×0.0283168 取 3 位；
    // estimate 沿用紅字規則。
    const cuft = item.volumeCuft;
    const volMethod = item.volumeMethod || item.product?.volumeMethod || 'auto';
    const volColor = cuft != null && volMethod === 'estimate' ? C.red : C.dark;
    writeText(curRow, 9, cuft != null ? `${fmt(cuft, 2)}cuft` : '—', { ...FONT, size: SZ.td, color: { argb: volColor } });
    writeText(curRow, 10, cuft != null ? `${(cuft * 0.0283168).toFixed(3)}CBM` : '—', { ...FONT, size: SZ.td, color: { argb: volColor } });

    // K：條碼（快照 → populate 產品 fallback；numFmt '@' 防科學記號，同標準版 需求 R37）
    const rawBarcode = item.barcode != null && item.barcode !== '' ? item.barcode : item.product?.barcode;
    const barcodeStr = rawBarcode != null ? String(rawBarcode).trim() : '';
    writeText(curRow, 11, barcodeStr || '—', { ...FONT, size: SZ.spec, color: { argb: C.mid } }, 'center', { numFmt: '@' });

    // L／M：單價／箱單價（Q2=A 純數值 + 幣別 numFmt）。
    // 單價值＝ceilMoney(item.unitPrice)＝畫面／標準版 fmtMoney 印出來的同一個數；
    // 箱單價＝calcBoxPrice（需求 R165 Q1=A：ceilMoney(印出單價)×箱入數），與標準版箱價同源。
    const unitPrinted = ceilMoney(item.unitPrice, currency);
    const boxPrice = calcBoxPrice(item.unitPrice, item.pcsPerBox, currency);
    writeText(curRow, 12, unitPrinted != null ? unitPrinted : '—',
      { ...FONT, size: SZ.price, color: { argb: C.dark } }, 'center', unitPrinted != null ? { numFmt } : {});
    writeText(curRow, 13, boxPrice != null ? boxPrice : '—',
      { ...FONT, size: SZ.price, color: { argb: C.mid } }, 'center', boxPrice != null ? { numFmt } : {});

    // N：MOQ 原字串；O：備註（與標準版同一個 buildRemarkText）
    writeText(curRow, 14, item.moq || '—', { ...FONT, size: SZ.spec, color: { argb: C.dark } });
    writeText(curRow, 15, buildRemarkText(item), { ...FONT, size: SZ.td, color: { argb: C.dark } });

    curRow++;
  });

  // 交易條件 TRADE TERMS（A:O 合併，規則同標準版）
  sheet.getRow(curRow).height = 8;
  curRow++;
  const termsTitleRow = curRow;
  sheet.getRow(termsTitleRow).height = 22;
  sheet.mergeCells(termsTitleRow, 1, termsTitleRow, LAST);
  const termsTitle = cl(termsTitleRow, 1);
  termsTitle.value = '交易條件 TRADE TERMS';
  termsTitle.fill = solid(C.light);
  termsTitle.font = { ...FONT, size: SZ.h3, bold: true, color: { argb: C.mid } };
  termsTitle.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  termsTitle.border = { top: bs(C.border), left: bs(C.border), right: bs(C.border), bottom: bs(C.border, 'hair') };

  const termsBodyRow = termsTitleRow + 1;
  const notes = quote.transactionNotes
    || '1. 本報價單有效期限為30天。\n2. 付款條件：訂單確認後預付50%，出貨前付清尾款。\n3. 交貨條件：FOB 台北 (Taiwan)，運費另計。\n4. 交貨時間：訂單確認後 7-14 個工作天。\n5. 本報價不含稅金，如需開立發票請另加 5% 營業稅。';
  const termLines = (notes.match(/\n/g) || []).length + 1;
  sheet.getRow(termsBodyRow).height = Math.max(60, termLines * 20);
  sheet.mergeCells(termsBodyRow, 1, termsBodyRow, LAST);
  const termsBody = cl(termsBodyRow, 1);
  termsBody.value = notes;
  termsBody.fill = solid(C.light);
  termsBody.font = { ...FONT, size: SZ.body, color: { argb: C.dark } };
  termsBody.alignment = { vertical: 'top', horizontal: 'left', wrapText: true, indent: 1 };
  termsBody.border = { top: bs(C.border, 'hair'), left: bs(C.border), right: bs(C.border), bottom: bs(C.border) };

  sheet.pageSetup.printArea = `A1:O${termsBodyRow}`;

  // 下載（Q5=A：{報價單號}_預購表.xlsx，避免與標準版同名互蓋）
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${quote.quoteNumber || 'quote'}_預購表.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
